// Over-capacity advice for USB and disc content (ADR-021 §6): the content the
// customer is sending against the USABLE capacity of the media they picked.
// Advisory only. It never blocks a price or an order, because splitting content
// across several pieces per set is a legitimate order.

import { useTranslation } from 'react-i18next';

import {
  ATTRIBUTE_KEYS,
  mediaContentCapacityCheck,
  type FieldValues,
  type MediaContentSizes,
} from 'shared';

import { formatDecimalBytes } from '../i18n/formatters.js';

export function MediaCapacityAdvisory({
  config,
  sizes,
  capacityLabel,
}: {
  config: FieldValues;
  sizes: MediaContentSizes;
  capacityLabel: (value: string) => string;
}): JSX.Element | null {
  const { t } = useTranslation('quote');
  const check = mediaContentCapacityCheck(config, sizes);
  if (!check) return null;
  const onDrive = config[ATTRIBUTE_KEYS.media_type] === 'usb';
  return (
    <div className="media-capacity" role="status" aria-live="polite" aria-atomic="true">
      {check.over_capacity && (
        <div className="field-warning">
          <p className="media-capacity__lead">
            <strong>
              {t('mediaContent.capacity.over', {
                content: formatDecimalBytes(check.content_bytes),
                usable: formatDecimalBytes(check.capacity_bytes),
                capacity: capacityLabel(check.capacity_value),
              })}
            </strong>
          </p>
          <ul className="media-capacity__options">
            {check.larger_capacity_value && (
              <li>
                {t('mediaContent.capacity.larger', {
                  capacity: capacityLabel(check.larger_capacity_value),
                })}
              </li>
            )}
            <li>
              {t(
                onDrive
                  ? 'mediaContent.capacity.multipleDrives'
                  : 'mediaContent.capacity.multipleDiscs',
                {
                  count: check.pieces_needed,
                }
              )}
            </li>
            {check.usb_holds_it && <li>{t('mediaContent.capacity.usb')}</li>}
          </ul>
          <p className="media-capacity__note">{t('mediaContent.capacity.proceed')}</p>
        </div>
      )}
      {check.exfat_note && <p className="field-tip">{t('mediaContent.capacity.exfat')}</p>}
    </div>
  );
}
